import { useEffect, useRef } from "react";

import { useStableRef } from "./useStableRef";

/**
 * Runs an onChange callback whenever the provided value changes
 * - the callback is always called on initial mount
 * - in order to skip the initial callback, check if previousValue == undefined
 * - value change is based on strict equality (Object.is) or the provided equalityFn
 * - it is ok for the callback to trigger side-effects, as it is called from useEffect()
 * - safe for concurrent-rendering since onChange side-effects run in an effect and the previous value check prevents re-running onChange when the effect re-runs
 * - note: the onChange handler should use newValue, not the local value (which might be inconsistent with newValue in some corner cases)
 */
export function useOnValueChange<T>(
  value: T,
  onChange: (newValue: T, previousValue: T | undefined) => void,
  equalityFn?: (a: any, b: any) => boolean,
) {
  const previousValueRef = useRef<T | undefined>(undefined);
  const stableOnChange = useStableRef(onChange);
  const stableEqualityFn = useStableRef(equalityFn ?? Object.is);

  useEffect(() => {
    const prevValue = previousValueRef.current;
    if (!stableEqualityFn.current(value, prevValue)) {
      previousValueRef.current = value;
      stableOnChange.current(value, prevValue);
    }
  }, [stableEqualityFn, stableOnChange, value]);
}
